export interface ICharacterLanguageMetadata {
  name?: string;
  voice?: string;
  description?: string;
  starterText?: string;
  talkSubjects?: string[] | string;
}

export type ICharacterMetadata = Record<string, ICharacterLanguageMetadata>;

export interface IContentCreator {
  id: string;
  name: string;
}

export interface ICharacter {
  _id: string;
  name: string;
  description: string;
  behavior: string;
  createDate: Date;
  isActive: boolean;
  isPremium?: boolean;
  isForMission?: boolean;
  isAvailableInIran: boolean;
  creator?: IContentCreator;
  starterText?: string;
  jobTitle?: string;
  talkSubjects?: string[];
  metadata?: ICharacterMetadata;
}

export interface IErrorModel {
  _id: string;
  date: Date;
  filename: string;
  details: any;
  isArchive: boolean;
  parameters?: {
    body?: any;
    userID?: string;
    [key: string]: any;
  };
}

export interface ISetting {
  key: string;
  title: string;
  value: string;
  type: string;
}

export interface IUser {
  _id?: string;
  name?: string;
  email?: string;
  password?: string;
  city?: string;
  country?: string;
  countryCode?: string;
  registerDate?: Date;
  originLanguage?: string;
  targetLanguage?: string;
  isEmailVerified?: boolean;
  verificationCode?: string;
  jobTitle?: string;

  /**
   * ### How it works:
   * when day change, update `lastUsageCounterDate` and reset `usageCount`
   * the counter works according to the `userDeviceID` to prevent user to reset
   * his/her limit by removing or clearing app data
   */
  lastUsageCounterDate?: Date;
  usageCount?: number;
  availableMessagesCount?: number;
  userDeviceID?: string;
  installedVersion?: string;
  runTimes?: number; // how many time the user used the app
  fcmToken?: string;
  fcmExpired?: boolean;
  referralCode?: number;
  installedFromReferral?: number;
  installedFrom?: string; //'google' | 'bazaar'
  lastRunTime?: Date;
  bio?: string;
  languageLevel?: number | string;
  goals?: string;
  learningReasons?: string[];
  dailyCommitmentMinutes?: number;
  isFirstMissionPassed?: boolean;
  xp?: number;
  progressionLevel?: number;
  totalMissionsCompleted?: number;
  currentStreak?: number;
  bestStreak?: number;
  lastActivityDate?: Date;
  friendsCount?: number;
  avatar?: string;
  gender?: "male" | "female";
  isGenderMale?: "male" | "female";
  // User connection counts
  followersCount?: number;
  followingCount?: number;
  // Whether the current user follows this user
  isFollowedByMe?: boolean;
  isFollowRequested?: boolean;
  // beta features
  betaFeatures?: Record<string, boolean>;
  // Premium and usage fields
  isPremiumUser?: boolean;
  isTesterUser?: boolean;
  premiumExpiresAt?: string | Date | null;
  premiumSource?: "apple" | "google" | "bazaar" | "manual";
  hasFreeSubscription?: boolean;
  dailyUsedMessagesCount?: number;
  totalMessagesCount?: number;
  dailyPartnerFindCount?: number;
  isDeleted?: boolean;
  isVerified?: boolean;
  isLevelVerified?: boolean;
  isBanned?: boolean;
  banExpireDate?: Date | string;
  banReason?: string;
  isTempUser?: boolean;
}

export interface IMessage {
  messageID?: number;
  from: number | IUser;
  to: number | ICharacter;
  message: string;
  sendDate?: Date;
  role: "user" | "assistant";
  messageTokenCount?: number;
  totalTokenCount?: number;
}

export interface TtsVoice {
  languageCodes: string[];
  name: string;
  ssmlGender: string;
  naturalSampleRateHertz: number;
}

export enum ReportCategory {
  SPAM = "spam",
  HARASSMENT = "harassment",
  INAPPROPRIATE_CONTENT = "inappropriate_content",
  FAKE_PROFILE = "fake_profile",
  OFFENSIVE_LANGUAGE = "offensive_language",
  SCAM = "scam",
  OTHER = "other",
}

export enum ReportStatus {
  PENDING = "pending",
  UNDER_REVIEW = "under_review",
  RESOLVED = "resolved",
  REJECTED = "rejected",
}

export enum ReportType {
  USER = "user",
  MESSAGE = "message",
}

export interface IReport {
  _id: string;
  reporterUserID: string | IUser;
  targetUserID: string | IUser;
  messageID?: string;
  chatID?: string;
  reportType: ReportType;
  category: ReportCategory;
  description?: string;
  status: ReportStatus;
  createdDate: Date;
  updatedDate: Date;
  reviewedBy?: string | IUser;
  reviewNotes?: string;
}

export interface ISuggestion {
  _id: string;
  forUserID: string | IUser;
  suggestedUserID: string | IUser;
  scheduledAt?: Date;
  createdAt?: Date;
  isProcessed?: boolean;
}

export enum NotificationType {
  FOLLOW_REQUEST = "follow_request",
  FOLLOW_ACCEPT = "follow_accepted",
  PARTNER_SUGGESTION = "partner_suggestion",
  OTHER = "other",
  SURVEY = "survey",
  IS_TYPING = "is_typing",
  SEEN = "seen",
  MESSAGE = "message",
  LOGOUT = "logout",
}

export type SurveyQuestionType = "radio" | "check" | "input" | "text" | "radioOrInput";

export interface ISurveyQuestion {
  questionId?: string;
  question: string;
  type: SurveyQuestionType;
  options?: string[];
}

export interface ISurveyQuestionMetadata {
  question?: string;
  options?: string[];
}

export interface ISurveyLanguageMetadata {
  title?: string;
  description?: string;
  questions?: Record<string, ISurveyQuestionMetadata>;
}

export type ISurveyMetadata = Record<string, ISurveyLanguageMetadata>;

export interface ISurvey {
  _id: string;
  title: string;
  description?: string;
  questions: ISurveyQuestion[];
  metadata?: ISurveyMetadata;
  rtl?: boolean;
  isActive?: boolean;
  responseCount?: number;
  createdAt?: string;
}

export interface ISurveyListResponse {
  data: ISurvey[];
  total: number;
}

export interface ISurveyResponseUser {
  _id?: string;
  name?: string;
  email?: string;
  originLanguage?: string;
}

export interface ISurveyResponse {
  _id: string;
  userID?: string | ISurveyResponseUser;
  language?: string;
  answers: { questionId: string; value: string | string[] }[];
  createdAt?: string;
}

export interface IWord {
  _id: string;
  userID: string | IUser;
  word: string;
  translate: string;
  fromLang: string;
  toLang: string;
  learnLevel: number;
  createdAt?: Date;
}

export interface IMediaFile {
  _id?: string;
  originalName?: string;
  url?: string;
  physicalPath?: string;
  folder?: string;
  mimeType?: string;
  size?: number;
  uploadDate?: string;
}

export interface ICloudFolder {
  name: string;
  path: string;
}

export interface ICloudFile {
  key: string;
  folder: string;
  size: number;
  lastModified?: string;
  inDb: boolean;
  fileId?: string;
}

export interface IMediaFolder {
  _id?: string;
  path: string;
  name: string;
  parentPath?: string | null;
  isSystem?: boolean;
  createDate?: string;
}

export interface IXpRewardByLevel {
  beginner?: number;
  intermediate?: number;
  advanced?: number;
}

export interface IChatLessonStepLevelTasks {
  user?: string[];
  ai?: string[];
}

export interface IChatLessonStepTasksByLevel {
  beginner?: IChatLessonStepLevelTasks;
  intermediate?: IChatLessonStepLevelTasks;
  advanced?: IChatLessonStepLevelTasks;
}

export interface IChatLessonStepLanguageMetadata {
  title?: string;
  voiceName?: string;
  starterText?: string;
}

export type IChatLessonStepMetadata = Record<
  string,
  IChatLessonStepLanguageMetadata
>;

export interface IChatLessonStep {
  type: "chat";
  stepId?: string;
  metadata?: IChatLessonStepMetadata;
  content?: string;
  voiceUrl?: string;
  canSkip?: boolean;
  character?: string;
  starterText?: string;
  inputTypes?: ("voice" | "text")[];
  courseTemplateId?: string;
  speakingRate?: number;
  avatar?: {
    sm?: string;
    md?: string;
  };
  context?: string;
  tasksByLevel?: IChatLessonStepTasksByLevel;
}

export interface IChatLessonCourseTemplate {
  _id: string;
  name: string;
  template: string;
  createDate?: string;
}

export enum ChatLessonTopic {
  DailyConversation = "daily_conversation",
  Travel = "travel",
  Work = "work",
  Study = "study",
  ExamPreparation = "exam_preparation",
}

export const CHAT_LESSON_TOPIC_OPTIONS: {
  label: string;
  value: ChatLessonTopic;
}[] = [
  { label: "Daily conversation", value: ChatLessonTopic.DailyConversation },
  { label: "Travel", value: ChatLessonTopic.Travel },
  { label: "Work", value: ChatLessonTopic.Work },
  { label: "Study", value: ChatLessonTopic.Study },
  { label: "Exam preparation", value: ChatLessonTopic.ExamPreparation },
];

export interface IChatLessonLanguageMetadata {
  title?: string;
  description?: string;
}

export type IChatLessonMetadata = Record<string, IChatLessonLanguageMetadata>;

export interface IChatLesson {
  _id: string;
  /** Admin list label only; app uses metadata titles. */
  title: string;
  metadata?: IChatLessonMetadata;
  topic?: ChatLessonTopic;
  isPremium?: boolean;
  isActive?: boolean;
  creator?: IContentCreator;
  isForJourney?: boolean;
  thumbnail?: {
    sm?: string;
    md?: string;
  };
  avatar?: {
    sm?: string;
    md?: string;
  };
  estimatedDuration?: number;
  xpReward?: number;
  xpRewardByLevel?: IXpRewardByLevel;
  introduction?: string;
  /** Admin's original AI-generate prompt; kept separate from metadata title/description. */
  aiGenerateTitle?: string;
  aiGenerateDescription?: string;
  steps?: IChatLessonStep[];
  createDate?: Date;
  isDeleted?: boolean;
}

export enum JourneyStepType {
  ChatLesson = "chat_lesson",
  WordLearning = "word_learning",
  CharacterTalk = "character_talk",
  LevelTest = "level_test",
  PartnerTalk = "partner_talk",
  MediaTalk = "media_talk",
}

export const JOURNEY_STEP_TYPE_OPTIONS: {
  label: string;
  value: JourneyStepType;
}[] = [
  { label: "Chat lesson", value: JourneyStepType.ChatLesson },
  { label: "Word learning", value: JourneyStepType.WordLearning },
  { label: "Character talk", value: JourneyStepType.CharacterTalk },
  { label: "Media talk", value: JourneyStepType.MediaTalk },
  { label: "Level test", value: JourneyStepType.LevelTest },
  { label: "Partner talk", value: JourneyStepType.PartnerTalk },
];

export enum JourneyStepMediaType {
  Image = "image",
  Voice = "voice",
  Video = "video",
}

export const JOURNEY_STEP_MEDIA_TYPE_OPTIONS: {
  label: string;
  value: JourneyStepMediaType;
}[] = [
  { label: "Image", value: JourneyStepMediaType.Image },
  { label: "Voice", value: JourneyStepMediaType.Voice },
  { label: "Video", value: JourneyStepMediaType.Video },
];

/** How the learner may respond during a media_talk step. */
export enum JourneyStepInputType {
  Voice = "voice",
  Text = "text",
  Both = "both",
}

export const JOURNEY_STEP_INPUT_TYPE_OPTIONS: {
  label: string;
  value: JourneyStepInputType;
}[] = [
  { label: "Mic only", value: JourneyStepInputType.Voice },
  { label: "Text only", value: JourneyStepInputType.Text },
  { label: "Mic or text", value: JourneyStepInputType.Both },
];

export interface IJourneyStepWord {
  word?: string;
  translation?: string;
}

export interface IJourneyStepLanguageMetadata {
  title?: string;
  description?: string;
  starterText?: string;
  words?: IJourneyStepWord[];
}

export type IJourneyStepMetadata = Record<string, IJourneyStepLanguageMetadata>;

export interface IJourneyLanguageMetadata {
  title?: string;
  description?: string;
}

export type IJourneyMetadata = Record<string, IJourneyLanguageMetadata>;

export interface IJourneyStepAiGenerateConfig {
  description?: string;
  stepCustomInstructionHint?: string;
  whenBeDoneHint?: string;
  wordCount?: number;
}

export interface IJourneyStepConfigs {
  aiGenerate?: IJourneyStepAiGenerateConfig;
  [key: string]: unknown;
}

export interface IJourneyStep {
  stepId?: string;
  type: JourneyStepType;
  metadata?: IJourneyStepMetadata;
  isOptional?: boolean;
  order?: number;
  xpReward?: number;
  lessonId?: string;
  wordCardId?: string;
  characterId?: string;
  minDurationMinutes?: number;
  whenBeDone?: string;
  stepCustomInstruction?: string;
  mediaFile?: {
    sm?: string;
    md?: string;
  };
  mediaType?: JourneyStepMediaType;
  inputType?: JourneyStepInputType;
  configs?: IJourneyStepConfigs;
  subSteps?: IJourneyStep[];
}

export interface IJourney {
  _id?: string;
  /** Admin list label; app uses metadata titles. */
  title: string;
  metadata?: IJourneyMetadata;
  topic: ChatLessonTopic[];
  isActive?: boolean;
  isPremium?: boolean;
  creator?: IContentCreator;
  isDeleted?: boolean;
  /** Minimum age (in years) a user must have to see/access this journey. */
  minAgeSupport?: number;
  photo?: {
    sm?: string;
    md?: string;
  };
  /** Played once in-app right after the user first picks this journey, before landing on home. */
  startVideoUrl?: string;
  steps?: IJourneyStep[];
  createDate?: Date;
  statistics?: {
    usersCount: number;
    completedUsersCount: number;
    inProgressUsersCount: number;
    leftUsersCount: number;
    completedStepsCount: number;
    totalStepsCount: number;
    todayUsersCount?: number;
    todayCompletedUsersCount?: number;
    todayInProgressUsersCount?: number;
    todayLeftUsersCount?: number;
  };
}

export interface IWordCardSample {
  text?: string;
  highlight?: string;
}

export interface IWordCardLangEntry {
  word?: string;
  partOfSpeech?: string;
  more?: string[];
  samples?: IWordCardSample[];
}

export interface IWordCardWord {
  en?: IWordCardLangEntry;
  es?: IWordCardLangEntry;
  fr?: IWordCardLangEntry;
  de?: IWordCardLangEntry;
  it?: IWordCardLangEntry;
  tr?: IWordCardLangEntry;
  fa?: IWordCardLangEntry;
  ja?: IWordCardLangEntry;
  ko?: IWordCardLangEntry;
  zh?: IWordCardLangEntry;
}

export interface IWordCardWordsByLevel {
  A1?: IWordCardWord[];
  B1?: IWordCardWord[];
  C1?: IWordCardWord[];
}

export interface IWordCardLanguageMetadata {
  title?: string;
  description?: string;
}

export type IWordCardMetadata = Record<string, IWordCardLanguageMetadata>;

export interface IWordCard {
  _id?: string;
  title: string;
  metadata?: IWordCardMetadata;
  topic: string;
  category?: string[];
  photo?: string;
  avatarPrompt?: string;
  aiGenerateDescriptions?: string[];
  words?: IWordCardWordsByLevel;
  isActive?: boolean;
  creator?: IContentCreator;
  isDeleted?: boolean;
  createDate?: string;
}

export interface IJourneyListResponse {
  data: IJourney[];
  total: number;
  page: number;
  limit: number;
}
